<template>
    <div class="map-wrapper">
        <!-- Dark Mode Toggle -->
        <div class="toggle-container">
            <label>
                <input type="checkbox" v-model="isDarkMode" @change="toggleTileLayer" />
                Dark Mode
            </label>
        </div>

        <!-- Address Autocomplete -->
        <autocomplete :value="searchQuery" @input="searchQuery = $event.target.value" :search="searchAddress"
            :get-result-value="getResultValue" @change="onInputChange" @submit="onSuggestionSelect"
            @select="onSuggestionSelect" placeholder="Enter destination address..." class="search-bar autocomplete">
            <template #result="{ result, props, isHighlighted }">
                <li v-bind="props" :class="['autocomplete-result-item', { highlighted: isHighlighted }]">
                    {{ result.display_name }}
                </li>
            </template>
        </autocomplete>

        <!-- Leaflet Map -->
        <!-- v-if="!searchQuery" -->
        <l-map style="height: 400px" :zoom="mapZoom" :center="mapCenter" :options="{ attributionControl: false }"
            @click="onMapClick">
            <l-tile-layer :url="tileLayerUrl" />
            <l-marker v-if="markerPosition" :lat-lng="markerPosition" :icon="customIcon">
                <l-popup>
                    <p><strong>Selected Location</strong></p>
                    <p>{{ markerPosition.lat.toFixed(5) }}, {{ markerPosition.lng.toFixed(5) }}</p>
                </l-popup>
            </l-marker>
        </l-map>

        <!-- Coordinates Panel -->
        <!-- <div v-if="markerPosition" class="info-panel">
            <p><strong>Latitude:</strong> {{ markerPosition.lat }}</p>
            <p><strong>Longitude:</strong> {{ markerPosition.lng }}</p>
            <button class="save-button" @click="saveAddress">Save Destination</button>
        </div> -->
    </div>
</template>

<script setup>
import { ref, defineEmits } from 'vue'
import { LMap, LTileLayer, LMarker, LPopup } from '@vue-leaflet/vue-leaflet'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import Autocomplete from '@trevoreyre/autocomplete-vue'
import '@trevoreyre/autocomplete-vue/dist/style.css'


const emit = defineEmits(["update:location"]);
// Fix default Leaflet icon paths
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
    iconRetinaUrl: new URL('leaflet/dist/images/marker-icon-2x.png', import.meta.url).href,
    iconUrl: new URL('leaflet/dist/images/marker-icon.png', import.meta.url).href,
    shadowUrl: new URL('leaflet/dist/images/marker-shadow.png', import.meta.url).href,
})

const customIcon = L.icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/684/684908.png',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
})

// Map State
const mapCenter = ref([30.3753, 69.3451]) // Pakistan center
const mapZoom = ref(5)
const markerPosition = ref(null)
const searchQuery = ref('')
const isDarkMode = ref(false)

// Tile Layers
const tileLayerLight = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png'
const tileLayerDark = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
const tileLayerUrl = ref(tileLayerLight)

const toggleTileLayer = () => {
    tileLayerUrl.value = isDarkMode.value ? tileLayerDark : tileLayerLight
}

// Debounced searchAddress for autocomplete
let searchTimeout
const searchAddress = (query) => {
    return new Promise((resolve) => {
        clearTimeout(searchTimeout)

        if (!query) {
            resolve([])
            return
        }

        searchTimeout = setTimeout(async () => {
            try {
                const res = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)},`
                )
                const data = await res.json()
                const results = data.map((item) => ({
                    display_name: item.display_name,
                    lat: item.lat,
                    lon: item.lon,
                }))
                resolve(results)
            } catch (error) {
                console.error('Search error:', error)
                resolve([])
            }
        }, 400) // 400ms debounce
    })
}

const getResultValue = (result) => result.display_name

const onSuggestionSelect = (result) => {
    if (!result || !result.lat || !result.lon) return

    const lat = parseFloat(result.lat)
    const lon = parseFloat(result.lon)
    markerPosition.value = { lat, lng: lon }
    mapCenter.value = [lat, lon]
    mapZoom.value = 15
    searchQuery.value = result.display_name
    emit("update:location", result)
}

const onMapClick = async (e) => {
    const { lat, lng } = e.latlng
    markerPosition.value = { lat, lng }
    mapCenter.value = [lat, lng]
    mapZoom.value = 15

    try {
        const res = await fetch(
            ` https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
        )
        const data = await res.json()
        if (data?.display_name) {
            searchQuery.value = data.display_name
            // Emit selected location from map click
            emit("update:location", {
                display_name: data.display_name,
                lat: lat,
                lon: lng,
            });
        }
    } catch (err) {
        console.error('Reverse geocoding failed:', err)
    }
}
const onInputChange = () => {
    // Only emit if no marker was selected (i.e. not from suggestion)
    if (searchQuery.value && !markerPosition.value) {
        emit("update:location", searchQuery.value);
    }
};



// const saveAddress = () => {
//     if (markerPosition.value) {
//         alert(
//             `Saved Location:\nLatitude: ${markerPosition.value.lat}\nLongitude: ${markerPosition.value.lng},`)
//         // Add backend API call here
//     }
// }
</script>

<style scoped>
/* .map-wrapper {
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
} */

.toggle-container {
    text-align: right;
    margin-bottom: 10px;
}

.toggle-container label {
    display: inline-flex;
    align-items: center;
    font-size: 14px;
    font-weight: 600;
    background-color: #e6e6e6;
    padding: 6px 12px;
    border-radius: 8px;
    cursor: pointer;
    gap: 6px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.autocomplete {
    position: relative;
    z-index: 1000;
    width: 100%;
    margin-bottom: 15px;
}

.autocomplete .autocomplete-results {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ccc;
    max-height: 250px;
    overflow-y: auto;
    margin-top: 4px;
    padding: 0;
    list-style: none;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    border-radius: 4px;
    z-index: 1001;
}

.autocomplete-result-item {
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
    transition: background-color 0.2s;
    font-size: 14px;
}

.autocomplete-result-item:hover,
.highlighted {
    background-color: #f5f5f5;
}

.info-panel {
    margin-top: 20px;
    font-size: 14px;
    background: #f9f9f9;
    padding: 15px;
    border-radius: 8px;
    border: 1px solid #ddd;
}

.save-button {
    margin-top: 10px;
    padding: 8px 16px;
    background-color: #2c7be5;
    color: #fff;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: bold;
    transition: background 0.2s;
}

.save-button:hover {
    background-color: #1a68d1;
}
</style>
